import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchDisciplineConfig } from '../services/disciplineApi';

// Async thunk to load discipline config from backend
export const loadEditorConfig = createAsyncThunk(
  'editorConfig/load',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchDisciplineConfig();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const editorConfigSlice = createSlice({
  name: 'editorConfig',
  initialState: {
    loaded: false,
    loading: false,
    error: null,
    // Active discipline (currently Elektrik)
    activeDisciplineId: null,
    disciplines: [],
    // Derived from active discipline
    sheetTypes: [],       // [{ id, code, name, order }]
    editorTools: [],      // [{ id, code, name, category, component, sheetAccess, order }]
    partyTypes: [],       // [{ id, code, name, category, isProjectOwner, toolSheetAccess }]
    // User plan
    userPlanId: null,
    userPlanCode: 'free',
    // Selected party type (for tool access filtering)
    activePartyId: null,
  },
  reducers: {
    setActiveDiscipline(state, action) {
      const discId = action.payload;
      state.activeDisciplineId = discId;
      const disc = state.disciplines.find(d => d.id === discId);
      if (disc) {
        state.sheetTypes = disc.sheets;
        state.editorTools = disc.tools;
        state.partyTypes = disc.parties;
        // Auto-select project owner party
        const ownerParty = disc.parties.find(p => p.isProjectOwner);
        if (ownerParty && !state.activePartyId) {
          state.activePartyId = ownerParty.id;
        }
      }
    },
    setActiveParty(state, action) {
      state.activePartyId = action.payload;
    },
    /**
     * Hydrate editor config from share token resolution response.
     * Used when an anonymous user opens a share link — no auth needed.
     * payload: { partyType, sheetTypes }
     */
    hydrateFromShare(state, action) {
      const { partyType, sheetTypes } = action.payload;
      state.sheetTypes = sheetTypes || [];
      state.partyTypes = partyType ? [partyType] : [];
      state.activePartyId = partyType?.id || null;
      state.loaded = true;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadEditorConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadEditorConfig.fulfilled, (state, action) => {
        const { disciplines, userPlanId, userPlanCode } = action.payload;
        state.disciplines = disciplines;
        state.userPlanId = userPlanId;
        state.userPlanCode = userPlanCode;
        state.loading = false;
        state.loaded = true;

        // Auto-select first active discipline (Elektrik)
        if (disciplines.length > 0 && !state.activeDisciplineId) {
          const disc = disciplines[0];
          state.activeDisciplineId = disc.id;
          state.sheetTypes = disc.sheets;
          state.editorTools = disc.tools;
          state.partyTypes = disc.parties;
          // Auto-select project owner party
          const ownerParty = disc.parties.find(p => p.isProjectOwner);
          if (ownerParty) {
            state.activePartyId = ownerParty.id;
          }
        }
      })
      .addCase(loadEditorConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Config yüklenemedi';
      });
  },
});

export const { setActiveDiscipline, setActiveParty, hydrateFromShare } = editorConfigSlice.actions;

// Selectors
export const selectSheetTypes = (state) => state.editorConfig.sheetTypes;
export const selectEditorTools = (state) => state.editorConfig.editorTools;
export const selectPartyTypes = (state) => state.editorConfig.partyTypes;
export const selectActiveParty = (state) => {
  const { partyTypes, activePartyId } = state.editorConfig;
  return partyTypes.find(p => p.id === activePartyId) || null;
};
export const selectEditorConfigLoaded = (state) => state.editorConfig.loaded;

/**
 * Returns a Set of tool IDs that are accessible for the given sheet code.
 * Based on the active party's toolSheetAccess matrix.
 */
export const selectToolAccessForSheet = (sheetCode) => (state) => {
  const party = selectActiveParty(state);
  if (!party) return new Set();
  const toolAccess = party.toolSheetAccess || {};
  const accessible = new Set();
  for (const [toolId, sheets] of Object.entries(toolAccess)) {
    if (Array.isArray(sheets) && sheets.includes(sheetCode)) {
      accessible.add(toolId);
    }
  }
  return accessible;
};

/**
 * Returns true if a specific tool is accessible for the current active sheet.
 */
export const selectIsToolAccessible = (toolId, sheetCode) => (state) => {
  const party = selectActiveParty(state);
  if (!party) return true; // Default to accessible if no party configured
  const toolAccess = party.toolSheetAccess || {};
  const sheets = toolAccess[toolId];
  if (!sheets) return false;
  return Array.isArray(sheets) && sheets.includes(sheetCode);
};

export default editorConfigSlice.reducer;
